/**
 * Safety Events Hook
 *
 * FERP LIFE-CRITICAL: Real-time safety event monitoring with GraphQL subscriptions
 * Repository pattern strict enforcement for all data access
 *
 * Principles:
 * - Data-driven: All filtering from backend configuration
 * - Real-time: GraphQL subscription for event streaming
 * - Repository pattern: All mutations through safetyEventRepository
 * - Immutable audit trail: Complete event history tracking
 * - Life-critical safety: HIPAA §164.308 incident response compliance
 *
 * Regulatory Compliance:
 * - HIPAA §164.308: Incident response tracking
 * - HIPAA §164.514: De-identification thresholds
 * - FDA 21 CFR Part 11: Electronic signatures and audit trails
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useSubscription, useMutation } from '@apollo/client/react';
type ApolloError = Error;
import { gql } from '@apollo/client';
import { useCurrentTenant } from './useCurrentTenant';

// FERP Architecture: SafetyEventPrimitive from backend GraphQL schema
export interface SafetyEventPrimitive {
  // Core Identification
  eventId: string;
  tenantId: string;
  timestamp: string;
  createdAt: string;
  updatedAt: string;

  // Event Classification
  category: string;
  severity: string;
  eventType: string;
  ruleId: string;
  description: string;

  // Safety Scoring
  safetyScore: number;
  previousSafetyScore?: number;
  safetyScoreDelta?: number;

  // Affected Entities
  affectedEntities: AffectedEntity[];
  sagaExecutionId?: string;
  workflowInstanceId?: string;
  primitiveType?: string;
  primitiveInstanceId?: string;
  actorDescriptorId?: string;

  // Remediation Tracking (HIPAA §164.308)
  remediationRequired: boolean;
  remediationStatus?: string;
  remediationOwner?: string;
  remediationNotes?: string;
  remediationDeadline?: string;
  remediationCompletedAt?: string;

  // Escalation Tracking
  escalationTriggered: boolean;
  escalationPathId?: string;
  escalationLevel?: string;
  escalatedTo: string[];
  emergencyContactsNotified: boolean;
  escalationTimestamps: EscalationTimestamp[];

  // Evidence & Context
  evidence?: any;
  metadata?: any;
  relatedEventIds: string[];
  parentEventId?: string;

  // Audit Trail
  userId: string;
  auditTrailId: string;
  sessionId?: string;
  sourceSystem: string;
  sourceIp?: string;
}

export interface AffectedEntity {
  entityType: string;
  entityId: string;
  impactLevel: string;
  metadata?: any;
}

export interface EscalationTimestamp {
  action: string;
  timestamp: string;
  userId: string;
  notes?: string;
}

export interface SafetyEventFilters {
  severityThreshold?: string; // Critical, High, Medium, Low, Info
  categoryFilter?: string; // PatientSafety, FinancialFraud, LegalPrivilege
  emergencyOnly?: boolean;
  timeRangeMinutes?: number; // Last N minutes
}

export interface UseSafetyEventsOptions {
  tenantId?: string;
  filters?: SafetyEventFilters;
  enableSubscription?: boolean; // Auto-start subscription
  maxEvents?: number; // Limit event history (default: 100)
}

export interface UseSafetyEventsReturn {
  // State
  events: SafetyEventPrimitive[];
  criticalEvents: SafetyEventPrimitive[];
  isConnected: boolean;
  error: ApolloError | null;
  connectionAttempts: number;

  // Actions
  createEvent: (input: CreateSafetyEventInput) => Promise<SafetyEventPrimitive>;
  updateRemediation: (input: UpdateRemediationInput) => Promise<boolean>;
  triggerEscalation: (input: TriggerEscalationInput) => Promise<boolean>;
  requireRemediation: (eventId: string, owner?: string, deadlineHours?: number) => Promise<boolean>;
  clearEvents: () => void;
  reconnect: () => void;

  // Metrics
  totalEvents: number;
  criticalCount: number;
  highCount: number;
  mediumCount: number;
  lowCount: number;
}

export interface CreateSafetyEventInput {
  tenantId: string;
  category: string;
  severity: string;
  eventType: string;
  ruleId: string;
  description: string;
  safetyScore: number;
  affectedEntities?: AffectedEntityInput[];
  sagaExecutionId?: string;
  workflowInstanceId?: string;
  primitiveType?: string;
  primitiveInstanceId?: string;
  evidence?: any;
  metadata?: any;
}

export interface AffectedEntityInput {
  entityType: string;
  entityId: string;
  impactLevel: string;
  metadata?: any;
}

export interface UpdateRemediationInput {
  eventId: string;
  tenantId: string;
  status: string;
  notes?: string;
  owner?: string;
}

export interface TriggerEscalationInput {
  eventId: string;
  tenantId: string;
  escalationPathId: string;
  escalationLevel: string;
  escalatedTo: string[];
  emergencyContactsNotified: boolean;
}

// GraphQL Subscription for real-time safety events (Phase 4 implementation)
const SAFETY_EVENTS_SUBSCRIPTION = gql`
  subscription SafetyEventCreated(
    $tenant: String!
    $severityThreshold: String
    $categoryFilter: String
    $emergencyOnly: Boolean
  ) {
    safetyEventCreated(
      tenant: $tenant
      severityThreshold: $severityThreshold
      categoryFilter: $categoryFilter
      emergencyOnly: $emergencyOnly
    ) {
      eventId
      tenant
      category
      severity
      eventType
      safetyScore
      safetyScoreDelta
      remediationRequired
      escalationTriggered
      escalationLevel
      emergencyContactsNotified
      timestamp
      userId
      auditTrailId
      isStaged
      description
    }
  }
`;

// GraphQL Subscription for escalation alerts (Phase 4 implementation)
const SAFETY_ESCALATIONS_SUBSCRIPTION = gql`
  subscription SafetyEventEscalated(
    $tenant: String!
    $escalationLevelFilter: String
  ) {
    safetyEventEscalated(
      tenant: $tenant
      escalationLevelFilter: $escalationLevelFilter
    ) {
      eventId
      tenant
      category
      severity
      eventType
      safetyScore
      safetyScoreDelta
      remediationRequired
      escalationTriggered
      escalationLevel
      emergencyContactsNotified
      timestamp
      userId
      auditTrailId
      isStaged
      description
    }
  }
`;

// GraphQL Mutations (Phase 2 implementation)
const STAGE_SAFETY_EVENT = gql`
  mutation StageSafetyEvent($input: CreateSafetyEventInput!) {
    stageSafetyEvent(input: $input) {
      eventId
      tenantId
      category
      severity
      safetyScore
      timestamp
      isStaged
      auditTrailId
    }
  }
`;

const UPDATE_SAFETY_EVENT = gql`
  mutation UpdateSafetyEvent(
    $tenant: String!
    $eventId: String!
    $input: UpdateSafetyEventInput!
  ) {
    updateSafetyEvent(
      tenant: $tenant
      eventId: $eventId
      input: $input
    ) {
      eventId
      tenantId
      remediationStatus
      escalationTriggered
      escalationLevel
      updatedAt
    }
  }
`;

const PERSIST_SAFETY_EVENT = gql`
  mutation PersistSafetyEvent($tenant: String!, $eventId: String!) {
    persistSafetyEvent(tenant: $tenant, eventId: $eventId) {
      eventId
      tenantId
      isStaged
      updatedAt
    }
  }
`;

const FLUSH_SAFETY_EVENTS = gql`
  mutation FlushSafetyEvents($tenant: String!) {
    flushSafetyEvents(tenant: $tenant)
  }
`;

/**
 * Hook for real-time safety event monitoring
 *
 * REPOSITORY PATTERN: All mutations through GraphQL (no direct API calls)
 * LIFE-CRITICAL: Real-time event streaming for immediate incident response
 *
 * @example Basic usage
 * ```tsx
 * const { events, criticalEvents, createEvent, updateRemediation } = useSafetyEvents({
 *   filters: {
 *     severityThreshold: 'High',
 *     emergencyOnly: false,
 *   }
 * });
 *
 * // Display critical events in emergency banner
 * {criticalEvents.length > 0 && (
 *   <EmergencyAlertBanner events={criticalEvents} />
 * )}
 * ```
 *
 * @example Create safety event
 * ```tsx
 * const handleSafetyViolation = async () => {
 *   await createEvent({
 *     tenantId: 'tenant123',
 *     category: 'PatientSafety',
 *     severity: 'Critical',
 *     eventType: 'SafetyViolation',
 *     ruleId: 'RULE_PATIENT_HARM_001',
 *     description: 'Patient safety score dropped below threshold',
 *     safetyScore: 42.5,
 *   });
 * };
 * ```
 */
export function useSafetyEvents(options: UseSafetyEventsOptions = {}): UseSafetyEventsReturn {
  const { tenantId: currentTenantId } = useCurrentTenant();
  const tenantId = options.tenantId || currentTenantId;

  const [events, setEvents] = useState<SafetyEventPrimitive[]>([]);
  const [connectionAttempts, setConnectionAttempts] = useState(0);
  const maxEventsRef = useRef(options.maxEvents || 100);

  // GraphQL subscription for real-time events (Phase 4 implementation)
  const { data, loading, error } = useSubscription<Record<string, any>>(SAFETY_EVENTS_SUBSCRIPTION, {
    variables: {
      tenant: tenantId,
      severityThreshold: options.filters?.severityThreshold,
      categoryFilter: options.filters?.categoryFilter,
      emergencyOnly: options.filters?.emergencyOnly || false,
    },
    skip: !options.enableSubscription || !tenantId,
    onData: ({ data }) => {
      if (data.data?.safetyEventCreated) {
        setConnectionAttempts(0); // Reset on successful connection
      }
    },
    onError: (err: any) => {
      setConnectionAttempts((prev) => prev + 1);
      console.error('Safety events subscription error:', err);
    },
  });

  // GraphQL mutations (Phase 2 implementation)
  const [stageEventMutation] = useMutation<Record<string, any>>(STAGE_SAFETY_EVENT);
  const [updateEventMutation] = useMutation<Record<string, any>>(UPDATE_SAFETY_EVENT);
  const [persistEventMutation] = useMutation<Record<string, any>>(PERSIST_SAFETY_EVENT);
  const [flushEventsMutation] = useMutation<Record<string, any>>(FLUSH_SAFETY_EVENTS);

  // Process incoming events from subscription (Phase 4 implementation)
  useEffect(() => {
    if (data?.safetyEventCreated) {
      setEvents((prev) => {
        const newEvent = data?.safetyEventCreated;

        // Convert subscription notification to full SafetyEventPrimitive
        const fullEvent: SafetyEventPrimitive = {
          eventId: newEvent.eventId,
          tenantId: newEvent.tenant,
          timestamp: newEvent.timestamp,
          createdAt: newEvent.timestamp,
          updatedAt: newEvent.timestamp,
          category: newEvent.category,
          severity: newEvent.severity,
          eventType: newEvent.eventType,
          ruleId: '', // Fetch from full query if needed
          description: newEvent.description,
          safetyScore: newEvent.safetyScore,
          previousSafetyScore: newEvent.safetyScore - (newEvent.safetyScoreDelta || 0),
          safetyScoreDelta: newEvent.safetyScoreDelta,
          affectedEntities: [],
          remediationRequired: newEvent.remediationRequired,
          escalationTriggered: newEvent.escalationTriggered,
          escalationLevel: newEvent.escalationLevel,
          escalatedTo: [],
          emergencyContactsNotified: newEvent.emergencyContactsNotified,
          escalationTimestamps: [],
          relatedEventIds: [],
          userId: newEvent.userId,
          auditTrailId: newEvent.auditTrailId,
          sourceSystem: 'FERP',
        };

        const updatedEvents = [fullEvent, ...prev];

        // Limit event history to maxEvents
        return updatedEvents.slice(0, maxEventsRef.current);
      });
    }
  }, [data]);

  // Calculate critical events (Critical or High severity)
  const criticalEvents = events.filter(
    (event) => event.severity === 'Critical' || event.severity === 'High'
  );

  // Create safety event (Phase 2: stageSafetyEvent)
  const createEvent = useCallback(
    async (input: CreateSafetyEventInput): Promise<SafetyEventPrimitive> => {
      const result = await stageEventMutation({
        variables: { input },
      });

      if (!result.data?.stageSafetyEvent) {
        throw new Error('Failed to stage safety event');
      }

      const staged = result.data?.stageSafetyEvent;

      // Return minimal event (full event will come via subscription)
      return {
        eventId: staged.eventId,
        tenantId: staged.tenantId,
        timestamp: staged.timestamp,
        createdAt: staged.timestamp,
        updatedAt: staged.timestamp,
        category: staged.category,
        severity: staged.severity,
        eventType: input.eventType,
        ruleId: input.ruleId,
        description: input.description,
        safetyScore: staged.safetyScore,
        affectedEntities: input.affectedEntities || [],
        remediationRequired: false,
        escalationTriggered: false,
        escalatedTo: [],
        emergencyContactsNotified: false,
        escalationTimestamps: [],
        relatedEventIds: [],
        userId: '', // Will be populated by backend
        auditTrailId: staged.auditTrailId,
        sourceSystem: 'FERP',
      };
    },
    [stageEventMutation]
  );

  // Update remediation status (Phase 2: updateSafetyEvent)
  const updateRemediation = useCallback(
    async (input: UpdateRemediationInput): Promise<boolean> => {
      if (!tenantId) {
        throw new Error('Tenant ID is required');
      }

      const result = await updateEventMutation({
        variables: {
          tenant: tenantId,
          eventId: input.eventId,
          input: {
            remediationStatus: input.status,
            remediationNotes: input.notes,
            remediationOwner: input.owner,
          },
        },
      });

      return !!result.data?.updateSafetyEvent?.remediationStatus;
    },
    [tenantId, updateEventMutation]
  );

  // Trigger escalation (Phase 2: updateSafetyEvent)
  const triggerEscalation = useCallback(
    async (input: TriggerEscalationInput): Promise<boolean> => {
      if (!tenantId) {
        throw new Error('Tenant ID is required');
      }

      const result = await updateEventMutation({
        variables: {
          tenant: tenantId,
          eventId: input.eventId,
          input: {
            escalationTriggered: true,
            escalationPathId: input.escalationPathId,
            escalationLevel: input.escalationLevel,
            escalatedTo: input.escalatedTo,
            emergencyContactsNotified: input.emergencyContactsNotified,
          },
        },
      });

      return result.data?.updateSafetyEvent?.escalationTriggered || false;
    },
    [tenantId, updateEventMutation]
  );

  // Mark event as requiring remediation
  const requireRemediation = useCallback(
    async (eventId: string, owner?: string, deadlineHours?: number): Promise<boolean> => {
      if (!tenantId) {
        throw new Error('Tenant ID is required');
      }

      // Calculate deadline from hours
      const deadline = deadlineHours
        ? new Date(Date.now() + deadlineHours * 60 * 60 * 1000).toISOString()
        : undefined;

      const result = await updateEventMutation({
        variables: {
          tenant: tenantId,
          eventId,
          input: {
            remediationStatus: 'PendingReview',
            remediationOwner: owner,
            remediationDeadline: deadline,
          },
        },
      });

      return !!result.data?.updateSafetyEvent;
    },
    [tenantId, updateEventMutation]
  );

  // Clear event history
  const clearEvents = useCallback(() => {
    setEvents([]);
  }, []);

  // Reconnect to subscription
  const reconnect = useCallback(() => {
    setConnectionAttempts(0);
    // Subscription will auto-reconnect via Apollo Client
  }, []);

  // Calculate metrics
  const totalEvents = events.length;
  const criticalCount = events.filter((e) => e.severity === 'Critical').length;
  const highCount = events.filter((e) => e.severity === 'High').length;
  const mediumCount = events.filter((e) => e.severity === 'Medium').length;
  const lowCount = events.filter((e) => e.severity === 'Low').length;

  return {
    // State
    events,
    criticalEvents,
    isConnected: !loading && !error,
    error: error || null,
    connectionAttempts,

    // Actions
    createEvent,
    updateRemediation,
    triggerEscalation,
    requireRemediation,
    clearEvents,
    reconnect,

    // Metrics
    totalEvents,
    criticalCount,
    highCount,
    mediumCount,
    lowCount,
  };
}
